import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { BuildResult } from "@sandbox/transpiler";
import { initTranspiler, transpile, transpileTests } from "./transpilerThunks";

export type TranspilerStatus = "ready" | "initializing" | "building" | "error" | undefined;
export type ProcessStatus = "building" | "loading-dependencies" | "idle";

interface TranspilerState {
  esbuildInitialized: boolean;
  swInitialized: boolean;
  buildResult?: BuildResult;
  testBuildResults?: BuildResult[];
  status?: TranspilerStatus;
  processStatus: ProcessStatus;
  previewLoaded: boolean;
}

const initialState: TranspilerState = {
  esbuildInitialized: false,
  swInitialized: false,
  previewLoaded: false,
  processStatus: "idle",
}

export const transpilerSlice = createSlice({
  name: "transpiler",
  initialState,
  reducers: {
    serviceWorkerInit: (state, action: PayloadAction<boolean>) => {
      state.swInitialized = action.payload;
    },
    transpilerStatusChanged: (state, { payload: status }: PayloadAction<TranspilerStatus>) => {
      state.status = status
    },
    processStatusChanged: (state, { payload: status }: PayloadAction<ProcessStatus>) => {
      state.processStatus = status
    },
    setPreviewLoaded: (state, { payload }: PayloadAction<boolean>) => {
      state.previewLoaded = payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(initTranspiler.fulfilled, (state) => {
      if (state.status === "initializing")
        state.status = "ready";
      state.esbuildInitialized = true;
    });
    builder.addCase(initTranspiler.rejected, (state, { error }) => {
      state.status = "error";
      state.buildResult = {
        errors: [{ text: `Failed to initialize esbuild. ${error.message}` }]
      }
      console.error(error);
    })
    builder.addCase(transpile.fulfilled, (state, { payload }) => {
      state.status = "ready";
      state.buildResult = payload;
    })
    builder.addCase(transpile.rejected, (state, { error }) => {
      state.status = "error";
      state.buildResult = {
        errors: [{ text: `Transpile failed. ${error.message}` }]
      }
      console.error(error);
    })
    builder.addCase(transpileTests.fulfilled, (state, { payload }) => {
      state.testBuildResults = payload;
    })
    builder.addCase(transpileTests.rejected, (_state, { error }) => {
      console.error(error);
    })
  }
});

// Action creators are generated for each case reducer function
export const {
  transpilerStatusChanged,
  processStatusChanged,
  serviceWorkerInit,
  setPreviewLoaded
} = transpilerSlice.actions;

export default transpilerSlice.reducer;
